import React, { useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageBanner } from '../../components/PageBanner/PageBanner';
import { PageNavigation, Tab } from '../../components/PageNavigation/PageNavigation';
import { EventCard } from '../../components/EventCard/EventCard';
import { CityFilter } from '../../components/CityFilter/CityFilter';
import ShowroomTabs, { ShowroomTab } from '../../components/ShowroomTabs/ShowroomTabs';
import { useEvents } from '../../hooks/useEvents';
import { adaptEventToCardEvent } from '../../adapters/eventAdapter';
import { useUserProfile } from '../../hooks/useUserProfile';
import './EventsPage.scss';

// Feature flag: Set to true to show Events, false to show "Coming Soon"
const SHOW_EVENTS = import.meta.env.VITE_SHOW_EVENTS === "true";

const EventsPage: React.FC = () => {
  const { t } = useTranslation('events');
  const { data: userData, isLoading } = useUserProfile();
  const [activeTab, setActiveTab] = useState('events');
  const [selectedCity, setSelectedCity] = useState('Montreal');
  const [selectedShowroom, setSelectedShowroom] = useState<ShowroomTab>('all');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for testingAppointments query parameter to bypass environment variable
  const isTestingMode = searchParams.get('testingAppointments') === 'true';
  const shouldShowEvents = SHOW_EVENTS || isTestingMode;
  
  // Only fetch events if the feature is enabled or testing mode is active
  const { data: events = [], isLoading: isEventsLoading } = shouldShowEvents ? useEvents(selectedCity) : { data: [], isLoading: false };

  if (isLoading || isEventsLoading) {
    return <Loader variant="fullscreen" />;
  }

  const handleTabChange = (tabId: string) => {
    if (tabId === 'booking') {
      navigate('/appointment');
    } else {
      setActiveTab(tabId);
    }
  };

  const eventsTabs: Tab[] = [
    { id: 'events', label: t('navigation.events') },
    { id: 'booking', label: t('navigation.booking') }
  ];

  return (
    <div className="events-page">
      <PageBanner
        title={t('banner.title')}
        subtitle={t('banner.subtitle')}
        userName={userData?.firstName || ''}
        showTagSaleButton={false}
        className="events-page__banner"
        userData={userData!}
      />
      <div className="events-page__content">
        <PageNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={eventsTabs}
        />
        {shouldShowEvents ? (
        <div className="events-page__inner-content">
          {activeTab === 'events' ? (
            <div className="events-page__events-content">
              <div className="filters-container">
                <CityFilter
                  cities={['Montreal', 'Toronto']}
                  selectedCity={selectedCity}
                  onCityChange={(city) => {
                    setSelectedCity(city);
                    setSelectedShowroom('all');
                  }}
                />
                {selectedCity === 'Montreal' && (
                  <ShowroomTabs
                    selectedTab={selectedShowroom}
                    onTabChange={setSelectedShowroom}
                    tabs={[
                      { id: 'all', label: t('showroom.tabs.all'), count: events.filter(e => e.city === selectedCity).length },
                      { id: 'MONTREAL SHOWROOM', label: t('showroom.montrealShowroom'), count: events.filter(e => e.city === selectedCity && e.showroom === 'MONTREAL SHOWROOM').length },
                      { id: 'ROYALMOUNT SHOWROOM', label: t('showroom.royalmountShowroom'), count: events.filter(e => e.city === selectedCity && e.showroom === 'ROYALMOUNT SHOWROOM').length }
                    ]}
                  />
                )}
              </div>
              <div className="events-page__events-grid">
                {(() => {
                  const filteredEvents = events.filter(event => {
                    // First filter by city
                    if (event.city !== selectedCity) return false;
                    // Then filter by showroom if in Montreal
                    if (selectedCity === 'Montreal') {
                      return selectedShowroom === 'all' || event.showroom === selectedShowroom;
                    }
                    return true;
                  });

                  return filteredEvents.length === 0 ? (
                    <div className="events-page__no-events">
                      <p>{t('noEvents')}</p>
                    </div>
                  ) : (
                    filteredEvents.map((event, index) => {
                      // Create a unique key using appointmentId or fallback to index
                      const uniqueKey = event.appointmentId || event.id || `event-${index}`;
                      return (
                        <EventCard
                          key={uniqueKey}
                          event={adaptEventToCardEvent(event)}
                          formVariant="event"
                          isAttended={event.isAttended}
                          isBooked={event.isBooked}
                        />
                      );
                    })
                  );
                })()} 
              </div>
            </div>
          ) : (
            <div className="events-page__booking-content">
              <div className="events-page__booking-instructions">
                <h3>{t('booking.title')}</h3>
                <p>{t('booking.description')}</p>
              </div>
              <div className="events-page__events-grid">
                {(() => {
                  const filteredEvents = events.filter(event => {
                    // First filter by city
                    if (event.city !== selectedCity) return false;
                    // Then filter by showroom if in Montreal
                    if (selectedCity === 'Montreal') {
                      return selectedShowroom === 'all' || event.showroom === selectedShowroom;
                    }
                    return true;
                  });

                  return filteredEvents.length === 0 ? (
                    <div className="events-page__no-events">
                      <p>{t('noEvents')}</p>
                    </div>
                  ) : (
                    filteredEvents.map((event, index) => {
                      // Create a unique key using appointmentId or fallback to index
                      const uniqueKey = event.appointmentId || event.id || `event-${index}`;
                      return (
                        <EventCard
                          key={uniqueKey}
                          event={adaptEventToCardEvent(event)}
                          formVariant="event"
                          isAttended={event.isAttended}
                          isBooked={event.isBooked}
                        />
                      );
                    })
                  );
                })()} 
              </div>
            </div>
          )}
        </div>
      ) : (
            <h1>{t('comingSoon')}</h1>
          )}
      </div>
    </div>
  );
};

export default EventsPage;
